import posthog from "posthog-js";
import { redactSensitiveSearch } from "@/lib/security";
import { storage } from "@/utils/storage";
import {
  buildEnv,
  resolvePostHogPrivacyConfig,
  type PostHogPrivacyConfig,
} from "@/config/thirdParty";

// PostHog only initialises when a project key is baked into the build.
const apiKey = (import.meta.env.VITE_POSTHOG_KEY as string | undefined)?.trim();
// On-prem builds have no implicit PostHog cloud host: a self-hosted
// VITE_POSTHOG_HOST must be provided alongside the key.
const apiHost =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined)?.trim() ||
  (import.meta.env.VITE_ONPREM === "true" ? "" : "https://us.i.posthog.com");
const privacy = resolvePostHogPrivacyConfig(buildEnv);

let initialized = false;

const SENSITIVE_PATH_RE = /^\/(invite|share)\/[^/]+/;

export function sanitizePath(pathname: string): string {
  return pathname.replace(SENSITIVE_PATH_RE, "/$1/[REDACTED]");
}

function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.pathname = sanitizePath(parsed.pathname);
    parsed.search = redactSensitiveSearch(parsed.search);
    return parsed.toString();
  } catch {
    return sanitizePath(url);
  }
}

export function sanitizeSearch(search: string): string {
  return redactSensitiveSearch(search);
}

type StoredUser = {
  id?: string | number;
  email?: string;
  username?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
};

type IdentifyInput = {
  email?: string | null;
  name?: string | null;
  is_staff?: boolean;
  is_superuser?: boolean;
};

/**
 * Person properties sent with `posthog.identify`. Email is PII and is only
 * included when `VITE_POSTHOG_IDENTIFY_EMAIL=true`.
 */
export function buildIdentifyProperties(
  user: IdentifyInput,
  config: Pick<PostHogPrivacyConfig, "identifyEmail"> = privacy
): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  if (user.name != null) properties.name = user.name;
  if (user.is_staff !== undefined) properties.is_staff = user.is_staff;
  if (user.is_superuser !== undefined)
    properties.is_superuser = user.is_superuser;
  if (config.identifyEmail && user.email) properties.email = user.email;
  return properties;
}

/** Init options derived from the privacy toggles (exported for tests). */
export function buildPrivacyInitOptions(
  config: PostHogPrivacyConfig = privacy
) {
  return {
    autocapture: config.autocapture,
    disable_session_recording: !config.sessionRecording,
    // PostHog otherwise injects <script> tags from us-assets.i.posthog.com
    // (remote config, web vitals, exception and dead-click capture). Our CSP
    // allows no third-party scripts, so they failed on every page load and
    // flooded /api/frontend-errors/. Config comes from the /flags API instead.
    disable_external_dependency_loading: true,
  };
}

function reidentifyFromStorage(): void {
  try {
    const raw = storage.getItem("user_info");
    if (!raw) return;
    const user = JSON.parse(raw) as StoredUser;
    if (user?.id == null) return;
    posthog.identify(
      String(user.id),
      buildIdentifyProperties({
        email: user.email,
        name: user.username,
        is_staff: user.is_staff,
        is_superuser: user.is_superuser,
      })
    );
  } catch {
    // ignore — best-effort reidentification
  }
}

export function initPostHog(): void {
  if (initialized || !apiKey || !apiHost) return;
  posthog.init(apiKey, {
    api_host: apiHost,
    capture_pageview: false, // we trigger pageview manually on route change
    capture_pageleave: true,
    // Privacy defaults: no autocapture / session recording unless opted in via
    // VITE_POSTHOG_AUTOCAPTURE / VITE_POSTHOG_SESSION_RECORDING.
    ...buildPrivacyInitOptions(),
    persistence: "localStorage+cookie",
    sanitize_properties(properties, _event) {
      if (typeof properties["$current_url"] === "string") {
        properties["$current_url"] = sanitizeUrl(properties["$current_url"]);
      }
      if (typeof properties["$pathname"] === "string") {
        properties["$pathname"] = sanitizePath(properties["$pathname"]);
      }
      if (typeof properties["url"] === "string") {
        properties["url"] = sanitizeUrl(properties["url"]);
      }
      return properties;
    },
    loaded: (ph) => {
      if (import.meta.env.DEV) ph.debug();
      reidentifyFromStorage();
    },
  });
  initialized = true;
}

export const isPostHogEnabled = (): boolean => initialized;

export { posthog };
